import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { addApi, addUser } from 'shared/src/api'
import { formatPriceUsd } from 'shared/src/utils/formatting'
import { RedemptionRequestInput } from 'shared/src/api/types'
import {
  addNavigate,
  addOptionalIntPathParam,
} from 'shared/src/utils/addNavigate'
import { typedAs } from 'shared/src/utils/typedAs'
import cx from 'classnames'

import { Balance } from './Balance'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Page } from 'components/Page'
import { addCmsLoadingAndErrorIndicators } from 'utils/addCmsLoadingAndErrorIndicators'
import { NumberFieldNew, TextAreaNew, TextFieldNew } from 'components/TextField'
import { Button } from 'components/Button'
import Icon from 'components/Icon'
import { BODY_MEDIUM_STYLE, SANS_SERIF_FONT_FAMILY } from 'components/styles'
import { makeAdminCreatorEarningsRoute } from 'utils/routes'
import { addToastContext } from 'components/toastContext'
import { Badge, getRedemptionDisplayStatus } from '.'

const useStyles = createUseStyles({
  balanceContainer: {
    display: 'flex',
    marginLeft: 34,
    gap: 48,
    marginBottom: 54,
  },
  urlContainer: {
    position: 'relative',
    display: 'flex',
    flexGrow: 1,
  },
  urlFieldContainer: {
    flexGrow: 1,
    height: 64,
  },
  urlInput: {
    ...BODY_MEDIUM_STYLE,
    color: 'rgba(54, 52, 50, 0.96)',
    width: '100%',
    height: '100%',
    background: 'rgba(54, 52, 50, 0.02)',
    borderRadius: 2,
    border: '1px solid rgba(144, 115, 79, 0.24)',
    paddingRight: 64,

    '&:focus': {
      outline: 'none',
      border: '1px solid rgba(144, 115, 79, 0.56)',
    },

    '&::placeholder': {
      color: 'rgba(54, 52, 50, 0.96)',
      opacity: 0.8,
    },
  },
  inputWithoutPasteIcon: {
    paddingLeft: 16,
  },
  inputWithPasteIcon: {
    paddingLeft: 56,
  },
  pasteIconContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    paddingLeft: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#CBA67A',
    pointerEvents: 'none',
  },
  urlTextField: {
    borderRight: 'none',
    width: 278,
  },
  submitUrlButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    height: 64,
    width: 64,
    background: 'rgba(54, 52, 50, 0.96)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0px 2px 2px 0px',
  },
  submutUrlButtonDisabled: {
    background: 'transparent',
    color: 'rgba(144, 115, 79, 0.24)',
  },
  notesInput: {
    height: 132,
  },
  rightChevron: {
    color: '#fff',
  },
  rightChevronDisabled: {
    color: 'rgba(144, 115, 79, 0.24)',
  },
  validatingUrl: {
    color: '#D99B71',
    fontSize: 14,
    lineHeight: '26px',
    fontWeight: 500,
    letterSpacing: 0.2,
    fontFamily: SANS_SERIF_FONT_FAMILY,
  },
  button: {
    width: '100%',
    height: 64,
  },
  cancelRequest: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontSize: 14,
    lineHeight: '140%',
    letterSpacing: 1.6,
    fontWeight: 800,
    color: '#90734F',
    textAlign: 'center',
    marginTop: 22,
  },
  badge: {
    height: 23,
    fontSize: 12,
    position: 'absolute',
    right: 24,
    top: 18,
    zIndex: 100,
  },
})

type Step = 'checkUrl' | 'fillForm'

export const RedemptionRequest: FC = flowMax(
  addDisplayName('RedemptionRequest'),
  addApi,
  addUser,
  addOptionalIntPathParam('userId'),
  addOptionalIntPathParam('redemptionId'),
  addDataLoadingFromPromise(
    'data',
    ({ userId, user, redemptionId, api }) =>
      Promise.all([
        api.creatorEarnings.creatorOverview(userId ?? user.id),
        redemptionId ? api.redemptionRequest.get(redemptionId) : null,
      ]),
    ['user', 'userId', 'redemptionId']
  ),
  addCmsLoadingAndErrorIndicators('data'),
  addProps(({ data: [creatorEarnings, redemptionRequest] }) => ({
    creatorEarnings,
    redemptionRequest,
  })),
  addState('step', 'setStep', ({ redemptionRequest }) =>
    typedAs<Step>(redemptionRequest ? 'fillForm' : 'checkUrl')
  ),
  addState('isVerifyingUrl', 'setIsVerifyingUrl', false),
  addProps(({ creatorEarnings, redemptionRequest }) => ({
    availableBalance:
      creatorEarnings.availableBalance + (redemptionRequest?.actualPrice ?? 0),
    editDisabled: redemptionRequest
      ? redemptionRequest.status !== 'unprocessed'
      : false,
  })),
  addState(
    'redemptionRequestInput',
    'setRedemptionRequestInput',
    ({ redemptionRequest }) => ({
      url: redemptionRequest?.url ?? '',
      inputPrice: redemptionRequest?.inputPrice.toString(),
      actualPrice: redemptionRequest?.actualPrice,
      size: redemptionRequest?.size,
      variant: redemptionRequest?.variant,
      email: redemptionRequest?.email,
      shippingAddress: redemptionRequest?.shippingAddress,
      notes: redemptionRequest?.notes,
    })
  ),
  addStateHandlers(
    ({ redemptionRequestInput }) => ({
      input: redemptionRequestInput,
    }),
    {
      onChangeUrl:
        ({ input }) =>
        (event: React.ChangeEvent<HTMLInputElement>) => ({
          input: { ...input, url: event.target.value },
        }),
      onChangeInputPrice:
        ({ input }) =>
        (inputPrice: string) => ({
          input: {
            ...input,
            inputPrice,
            actualPrice: parseFloat(inputPrice) * 1.08,
          },
        }),
      onChangeSize:
        ({ input }) =>
        (size: string) => ({
          input: { ...input, size },
        }),
      onChangeVariant:
        ({ input }) =>
        (variant: string) => ({
          input: { ...input, variant },
        }),
      onChangeEmail:
        ({ input }) =>
        (email: string) => ({
          input: { ...input, email },
        }),
      onChangeShippingAddress:
        ({ input }) =>
        (shippingAddress: string) => ({
          input: { ...input, shippingAddress },
        }),
      onChangeNotes:
        ({ input }) =>
        (notes: string) => ({
          input: { ...input, notes },
        }),
    }
  ),
  addNavigate,
  addToastContext,
  addTranslationHelpers,
  addHandlers({
    onCheckUrl:
      ({
        input: { url },
        setIsVerifyingUrl,
        setStep,
        api,
        showErrorToast,
        t,
      }) =>
      () => {
        if (url) {
          setIsVerifyingUrl(true)
          api.products
            .isAffiliatedProduct(url)
            .then((res) => {
              if (res.isAffiliated) {
                setStep('fillForm')
              } else {
                showErrorToast({
                  title: t(
                    'admin.redemptionRequests.redemptionRequestNotEligibleTitle'
                  ),
                  details: t(
                    'admin.redemptionRequests.redemptionRequestNotEligibleDetails'
                  ),
                })
              }
            })
            .catch(() => {
              showErrorToast({
                title: t(
                  'admin.redemptionRequests.redemptionRequestErrorTitle'
                ),
                details: t(
                  'admin.redemptionRequests.redemptionRequestErrorDetails'
                ),
              })
            })
            .finally(() => setIsVerifyingUrl(false))
        }
      },
    onSubmitOrUpdate:
      ({
        user,
        userId,
        input,
        api,
        navigate,
        redemptionId,
        showSuccessToast,
        showErrorToast,
        t,
      }) =>
      () => {
        if (
          !!input.url &&
          !!input.inputPrice &&
          !!input.actualPrice &&
          !!input.email &&
          !!input.shippingAddress
        ) {
          const requestInput: RedemptionRequestInput = {
            url: input.url,
            inputPrice: parseFloat(input.inputPrice),
            size: input.size,
            variant: input.variant,
            email: input.email,
            shippingAddress: input.shippingAddress,
            notes: input.notes,
            userId: userId ?? user.id,
            actualPrice: undefined,
            status: 'unprocessed',
          }

          if (redemptionId) {
            api.redemptionRequest
              .update(redemptionId, requestInput)
              .then(() => {
                showSuccessToast({
                  title: t(
                    'admin.redemptionRequests.redemptionRequestSuccessTitle'
                  ),
                  details: t(
                    'admin.redemptionRequests.redemptionRequestUpdateSuccessDetails'
                  ),
                })
              })
              .catch(() => {
                showErrorToast({
                  title: t(
                    'admin.redemptionRequests.redemptionRequestErrorTitle'
                  ),
                  details: t(
                    'admin.redemptionRequests.redemptionRequestErrorDetails'
                  ),
                })
              })
          } else {
            api.redemptionRequest
              .create(requestInput)
              .then(() => {
                navigate(
                  userId ? makeAdminCreatorEarningsRoute(userId) : '/earnings'
                )
                showSuccessToast({
                  title: t(
                    'admin.redemptionRequests.redemptionRequestSuccessTitle'
                  ),
                  details: t(
                    'admin.redemptionRequests.redemptionRequestCreateSuccessDetails'
                  ),
                })
              })
              .catch(() => {
                showErrorToast({
                  title: t(
                    'admin.redemptionRequests.redemptionRequestErrorTitle'
                  ),
                  details: t(
                    'admin.redemptionRequests.redemptionRequestErrorDetails'
                  ),
                })
              })
          }
        }
      },
    onCancel:
      ({
        redemptionId,
        userId,
        api,
        navigate,
        showSuccessToast,
        showErrorToast,
        t,
      }) =>
      () => {
        if (redemptionId) {
          if (
            window.confirm(
              t('admin.redemptionRequests.deleteRedemptionConfirmation')
            )
          ) {
            api.redemptionRequest
              .markAsDeleted(redemptionId)
              .then(() => {
                navigate(
                  userId ? makeAdminCreatorEarningsRoute(userId) : '/earnings'
                )
                showSuccessToast({
                  title: t(
                    'admin.redemptionRequests.redemptionRequestSuccessTitle'
                  ),
                  details: t(
                    'admin.redemptionRequests.redemptionRequestCancelSuccessDetails'
                  ),
                })
              })
              .catch(() => {
                showErrorToast({
                  title: t(
                    'admin.redemptionRequests.redemptionRequestErrorTitle'
                  ),
                  details: t(
                    'admin.redemptionRequests.redemptionRequestErrorDetails'
                  ),
                })
              })
          }
        }
      },
  }),
  addProps(({ input, availableBalance }) => ({
    submitOrUpdateDisabled:
      !input.url ||
      !input.inputPrice ||
      !input.email ||
      !input.shippingAddress ||
      !input.actualPrice ||
      input.actualPrice > availableBalance,
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    creatorEarnings: { currentBalance },
    availableBalance,
    editDisabled,
    redemptionRequest,
    step,
    isVerifyingUrl,
    onCheckUrl,
    onSubmitOrUpdate,
    onCancel,
    input,
    onChangeUrl,
    onChangeInputPrice,
    onChangeSize,
    onChangeVariant,
    onChangeEmail,
    onChangeShippingAddress,
    onChangeNotes,
    submitOrUpdateDisabled,
    classes,
    t,
  }) => (
    <Page leftButton="" title={t('admin.creatorFund.redeem')}>
      {step === 'checkUrl' && (
        <>
          <div className={classes.balanceContainer}>
            <Balance
              amount={currentBalance}
              label={t('admin.creatorFund.currentBalance')}
            />
            <Balance
              amount={availableBalance}
              label={t('admin.creatorFund.availableBalance')}
            />
          </div>
          <div className={classes.urlContainer}>
            <div className={classes.urlFieldContainer}>
              <input
                type="url"
                placeholder={t('admin.redemptionRequests.redeemUrl')}
                value={input.url}
                onChange={onChangeUrl}
                className={cx(
                  classes.urlInput,
                  input.url
                    ? classes.inputWithoutPasteIcon
                    : classes.inputWithPasteIcon
                )}
              />
            </div>
            {!input.url && (
              <div className={classes.pasteIconContainer}>
                <Icon name="copy" />
              </div>
            )}
            <button
              onClick={onCheckUrl}
              disabled={
                isVerifyingUrl || input.url === null || input.url === ''
              }
              className={cx(
                classes.submitUrlButton,
                (input.url === null || input.url === '') &&
                  classes.submutUrlButtonDisabled
              )}
            >
              <Icon
                name={isVerifyingUrl ? 'loading' : 'chevron-right'}
                className={cx(
                  classes.rightChevron,
                  (input.url === null || input.url === '') &&
                    classes.rightChevronDisabled
                )}
              />
            </button>
          </div>
          {isVerifyingUrl && (
            <div className={classes.validatingUrl}>
              {t('admin.redemptionRequests.validatingUrl')}
            </div>
          )}
        </>
      )}
      {step === 'fillForm' && (
        <>
          {redemptionRequest && (
            <Badge
              displayText={getRedemptionDisplayStatus(redemptionRequest.status)}
              className={classes.badge}
            />
          )}
          <TextFieldNew
            id="url"
            onChange={() => {}}
            value={input.url}
            disabled={true}
          />
          {!editDisabled && (
            <Balance
              amount={availableBalance}
              label={t('admin.creatorFund.availableBalance')}
            />
          )}
          <NumberFieldNew
            id="inputPrice"
            label={t('admin.redemptionRequests.price')}
            onChange={onChangeInputPrice}
            value={input.inputPrice}
            error={
              input.actualPrice && input.actualPrice > availableBalance
                ? t('admin.redemptionRequests.insufficientBalance', {
                    availableBalance: formatPriceUsd(availableBalance),
                    estimatedPrice: formatPriceUsd(input.actualPrice),
                  })
                : undefined
            }
            disabled={editDisabled}
          />
          <TextFieldNew
            id="size"
            label={t('admin.redemptionRequests.size')}
            onChange={onChangeSize}
            value={input.size}
            disabled={editDisabled}
          />
          <TextFieldNew
            id="variant"
            label={t('admin.redemptionRequests.variant')}
            onChange={onChangeVariant}
            value={input.variant}
            disabled={editDisabled}
          />
          <TextFieldNew
            id="email"
            label={t('admin.redemptionRequests.email')}
            onChange={onChangeEmail}
            value={input.email}
            disabled={editDisabled}
          />
          <TextFieldNew
            id="shippingAddress"
            label={t('admin.redemptionRequests.shippingAddress')}
            onChange={onChangeShippingAddress}
            value={input.shippingAddress}
            disabled={editDisabled}
          />
          <TextAreaNew
            id="notes"
            label={t('admin.redemptionRequests.notes')}
            onChange={onChangeNotes}
            value={input.notes}
            inputClassName={classes.notesInput}
            disabled={editDisabled}
          />
          {!redemptionRequest && (
            <Button
              onClick={onSubmitOrUpdate}
              text={t('admin.redemptionRequests.submit')}
              disabled={submitOrUpdateDisabled}
              className={classes.button}
            />
          )}
          {redemptionRequest?.status === 'unprocessed' && (
            <>
              <Button
                onClick={onSubmitOrUpdate}
                text={t('admin.redemptionRequests.update')}
                disabled={submitOrUpdateDisabled}
                className={classes.button}
              />
              <div onClick={onCancel} className={classes.cancelRequest}>
                {t('admin.redemptionRequests.cancel')}
              </div>
            </>
          )}
        </>
      )}
    </Page>
  )
)
